// Pre-flight check page — single-glance org readiness card the
// trainer (or each trainee on their own org) hits BEFORE the first
// pipeline run of the day. Wraps the existing /api/org/prereq-check
// endpoint with a generous, training-friendly UI:
//
//   * One big traffic-light header (green ready / amber warnings /
//     red blocked).
//   * Grouped check rows with a one-line fix hint and a deep-link to
//     the relevant Salesforce Setup page (the API already returns
//     ``setup_url`` relative paths — we prepend instance_url).
//   * "Re-run check" button with a 1.5s soft cooldown so impatient
//     re-clicking doesn't hammer Salesforce.
//
// Linked from the Landing page Help drawer for May-4 training so
// trainees can self-diagnose ("license missing", "TN not enabled",
// etc.) without flagging John down.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiSoft } from "@/lib/api/http";
import { useAuth } from "@/hooks/useAuth";

interface PrereqCheck {
  id: string;
  label: string;
  category: "required" | "recommended" | "info";
  status: "pass" | "fail" | "warn" | "skip";
  message: string;
  detail: string;
  fix_hint: string;
  setup_url: string;
}

interface PrereqReport {
  org_id: string;
  org_name: string;
  edition: string;
  api_version: string;
  instance_url: string;
  language: string;
  supported_locale: boolean;
  can_proceed: boolean;
  has_warnings: boolean;
  has_existing_data: boolean;
  existing_accounts: number;
  existing_opportunities: number;
  existing_cases: number;
  summary: { pass: number; fail: number; warn: number; skip: number };
  checks: PrereqCheck[];
}

const STATUS_STYLE: Record<
  PrereqCheck["status"],
  { dot: string; bg: string; fg: string; label: string }
> = {
  pass: { dot: "#16a34a", bg: "rgba(22,163,74,.06)", fg: "#15803d", label: "OK" },
  warn: { dot: "#d97706", bg: "rgba(217,119,6,.06)", fg: "#9a5b00", label: "Warn" },
  fail: { dot: "#dc2626", bg: "rgba(220,38,38,.06)", fg: "#a31515", label: "Fix" },
  skip: { dot: "#6b7280", bg: "rgba(107,114,128,.06)", fg: "#374151", label: "—" },
};

function HeroLight({ report }: { report: PrereqReport }): JSX.Element {
  let title: string;
  let subtitle: string;
  let bg: string;
  let fg: string;
  if (!report.can_proceed) {
    title = "Org not ready";
    subtitle = `${report.summary.fail} required check${report.summary.fail === 1 ? "" : "s"} failing — fix below before launch`;
    bg = "linear-gradient(135deg,#fee2e2,#fecaca 60%)";
    fg = "#7f1d1d";
  } else if (report.has_warnings) {
    title = "Ready with warnings";
    subtitle = `${report.summary.warn} warning${report.summary.warn === 1 ? "" : "s"} — non-blocking, but worth a glance`;
    bg = "linear-gradient(135deg,#fef3c7,#fde68a 60%)";
    fg = "#78350f";
  } else {
    title = "Org is ready ✓";
    subtitle = "All checks passed — you're clear for takeoff";
    bg = "linear-gradient(135deg,#dcfce7,#bbf7d0 60%)";
    fg = "#14532d";
  }
  return (
    <div
      style={{
        background: bg,
        color: fg,
        borderRadius: 18,
        padding: "26px 28px",
        marginBottom: 18,
        boxShadow: "0 8px 26px -10px rgba(0,0,0,.18)",
      }}
    >
      <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: -0.4 }}>
        {title}
      </div>
      <div style={{ fontSize: 14, marginTop: 4, opacity: 0.9 }}>{subtitle}</div>
      <div
        style={{
          marginTop: 14,
          fontSize: 12,
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <span>
          <strong>{report.org_name || report.org_id}</strong>
        </span>
        <span>· {report.edition || "Edition unknown"}</span>
        <span>· API v{report.api_version}</span>
        <span>· {report.language}</span>
        {report.has_existing_data && (
          <span>
            · existing data ({report.existing_accounts.toLocaleString()} accts /{" "}
            {report.existing_opportunities.toLocaleString()} opps /{" "}
            {report.existing_cases.toLocaleString()} cases)
          </span>
        )}
      </div>
    </div>
  );
}

function CheckRow({
  check,
  instanceUrl,
}: {
  check: PrereqCheck;
  instanceUrl: string;
}): JSX.Element {
  const sty = STATUS_STYLE[check.status];
  const setupHref = check.setup_url
    ? `${instanceUrl.replace(/\/$/, "")}${check.setup_url}`
    : null;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "12px 1fr auto",
        gap: 14,
        alignItems: "start",
        padding: "12px 14px",
        background: sty.bg,
        borderRadius: 10,
        border: `1px solid ${sty.fg}22`,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 10,
          height: 10,
          borderRadius: 999,
          marginTop: 6,
          background: sty.dot,
        }}
      />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: "var(--ink)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>{check.label}</span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: "1px 6px",
              borderRadius: 999,
              background: sty.fg + "22",
              color: sty.fg,
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            {sty.label}
          </span>
          {check.category === "required" && check.status !== "pass" && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: "#7f1d1d",
                opacity: 0.8,
              }}
            >
              required
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, marginTop: 3, color: "var(--ink)" }}>
          {check.message}
        </div>
        {check.fix_hint && check.status !== "pass" && (
          <div style={{ fontSize: 11, marginTop: 4, color: "var(--ink-2)" }}>
            <strong>Fix:</strong> {check.fix_hint}
          </div>
        )}
      </div>
      {setupHref && check.status !== "pass" && (
        <a
          href={setupHref}
          target="_blank"
          rel="noopener noreferrer"
          className="btn ghost sm"
          style={{ fontSize: 11, whiteSpace: "nowrap" }}
        >
          Open in Setup ↗
        </a>
      )}
    </div>
  );
}

export function Preflight(): JSX.Element {
  const auth = useAuth();
  const [report, setReport] = useState<PrereqReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [includeSales, setIncludeSales] = useState(true);
  const [includeService, setIncludeService] = useState(true);
  const [lastRunAt, setLastRunAt] = useState<number>(0);

  const run = async (): Promise<void> => {
    if (Date.now() - lastRunAt < 1500) return; // soft cooldown
    if (!auth.orgConnected) {
      setError("Connect a Salesforce org first.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await apiSoft<PrereqReport>(
        `/api/org/prereq-check?include_sales=${includeSales}&include_service=${includeService}`,
      );
      if (!data) throw new Error("Pre-flight check returned no data.");
      setReport(data);
      setLastRunAt(Date.now());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Pre-flight check failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.orgConnected && !report) {
      void run();
    }
    // Run is intentionally not in deps — we only want auto-run once
    // when an org session resolves on first mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.orgConnected]);

  const grouped = useMemo(() => {
    if (!report) return null;
    const required = report.checks.filter((c) => c.category === "required");
    const recommended = report.checks.filter((c) => c.category === "recommended");
    const info = report.checks.filter((c) => c.category === "info");
    return { required, recommended, info };
  }, [report]);

  if (!auth.loading && !auth.orgConnected) {
    return (
      <div className="page" style={{ maxWidth: 760 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, marginBottom: 8 }}>
          Pre-flight check
        </h1>
        <p style={{ color: "var(--ink-2)", marginBottom: 16 }}>
          Connect a Salesforce org first, then come back to verify
          everything's ready before launch.
        </p>
        <Link to="/" className="btn primary">
          Back to Build
        </Link>
      </div>
    );
  }

  return (
    <div className="page" style={{ maxWidth: 920 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: -0.3 }}>
            Pre-flight check
          </h1>
          <p style={{ color: "var(--ink-2)", fontSize: 13 }}>
            Verifies licenses, permissions, locale, and key features. Run
            this on a fresh org BEFORE your first pipeline of the day.
          </p>
        </div>
        <button
          className="btn primary"
          onClick={() => void run()}
          disabled={loading}
        >
          {loading ? "Running…" : report ? "Re-run check" : "Run check"}
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: 14,
          alignItems: "center",
          marginBottom: 18,
          fontSize: 13,
        }}
      >
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={includeSales}
            onChange={(e) => setIncludeSales(e.target.checked)}
          />
          Sales Cloud
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={includeService}
            onChange={(e) => setIncludeService(e.target.checked)}
          />
          Service Cloud
        </label>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: 12,
            background: "rgba(220,38,38,.08)",
            color: "#7f1d1d",
            border: "1px solid rgba(220,38,38,.2)",
            borderRadius: 10,
            marginBottom: 14,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {report && (
        <>
          <HeroLight report={report} />

          {grouped && grouped.required.length > 0 && (
            <section style={{ marginBottom: 18 }}>
              <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
                Required checks
              </h2>
              <div style={{ display: "grid", gap: 8 }}>
                {grouped.required.map((c) => (
                  <CheckRow
                    key={c.id}
                    check={c}
                    instanceUrl={report.instance_url}
                  />
                ))}
              </div>
            </section>
          )}

          {grouped && grouped.recommended.length > 0 && (
            <section style={{ marginBottom: 18 }}>
              <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
                Recommended
              </h2>
              <div style={{ display: "grid", gap: 8 }}>
                {grouped.recommended.map((c) => (
                  <CheckRow
                    key={c.id}
                    check={c}
                    instanceUrl={report.instance_url}
                  />
                ))}
              </div>
            </section>
          )}

          {grouped && grouped.info.length > 0 && (
            <section style={{ marginBottom: 18 }}>
              <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
                Info
              </h2>
              <div style={{ display: "grid", gap: 8 }}>
                {grouped.info.map((c) => (
                  <CheckRow
                    key={c.id}
                    check={c}
                    instanceUrl={report.instance_url}
                  />
                ))}
              </div>
            </section>
          )}

          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 22,
              paddingTop: 18,
              borderTop: "1px solid var(--bd)",
            }}
          >
            <div style={{ fontSize: 12, color: "var(--ink-2)" }}>
              {report.summary.pass} passed · {report.summary.warn} warnings ·{" "}
              {report.summary.fail} failed · {report.summary.skip} skipped
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Link to="/" className="btn ghost">
                Back
              </Link>
              <Link
                to="/configure"
                className="btn primary"
                aria-disabled={!report.can_proceed}
                style={{ opacity: report.can_proceed ? 1 : 0.5 }}
              >
                Continue to Configure →
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
